import React, { useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const STATUSES = ["started", "submitted", "under_review", "evaluation_sent", "evaluation_received", "accepted", "rejected", "withdrawn"];

export default function AdminApplications() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");
  const [evalDialog, setEvalDialog] = useState(false);
  const [evalMessage, setEvalMessage] = useState("");
  const [evalFile, setEvalFile] = useState(null);

  const load = () => api.get("/applications").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const runReminders = async () => {
    try {
      const { data } = await api.post("/admin/evaluation-reminders/run");
      toast.success(
        lang === "fr"
          ? `Rappels: ${data.sent} envoyé(s), ${data.skipped} ignoré(s)`
          : `Reminders: ${data.sent} sent, ${data.skipped} skipped`,
      );
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}/status`, { status, notes: note || undefined });
      toast.success("Updated · email logged");
      setNote("");
      setOpen(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendEvaluation = async () => {
    if (!evalMessage.trim()) { toast.error("Message requis"); return; }
    try {
      const payload = { message: evalMessage };
      if (evalFile) { payload.filename = evalFile.name; payload.base64 = evalFile.base64; }
      const { data } = await api.post(`/applications/${open.id}/evaluation/send`, payload);
      toast.success(lang === "fr" ? "Évaluation envoyée à la famille" : "Evaluation sent to the family");
      setEvalDialog(false); setEvalMessage(""); setEvalFile(null);
      setOpen(data);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const onPickEvalFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 2_500_000) { toast.error("Max 2.5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setEvalFile({ name: f.name, base64: reader.result });
    reader.readAsDataURL(f);
  };

  return (
    <div data-testid="admin-applications">
      <p className="overline mb-1">OFA France · Admin</p>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl tracking-tight">{t("admin.applications")}</h1>
        <Button data-testid="run-reminders-btn" variant="outline" size="sm" onClick={runReminders}>
          {lang === "fr" ? "Envoyer les rappels d'évaluation" : "Send evaluation reminders"}
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-3">Student</th>
                <th className="text-left p-3">Family</th>
                <th className="text-left p-3">Program</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id} data-testid={`app-row-${a.id}`} onClick={() => setOpen(a)} className="border-t border-border hover:bg-secondary/30 cursor-pointer">
                  <td className="p-3 font-medium">{a.student?.first_name} {a.student?.last_name}</td>
                  <td className="p-3 text-xs">{a.family?.parent_first_name} {a.family?.parent_last_name}<div className="text-muted-foreground">{a.family?.email}</div></td>
                  <td className="p-3 text-xs">{a.program_choice ? t(`programs.${a.program_choice}`) : "—"}</td>
                  <td className="p-3"><Badge>{t(`statuses.${a.status}`)}</Badge></td>
                  <td className="p-3 text-xs text-muted-foreground">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-muted-foreground">No applications</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl">
          {open && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-2xl">{open.student?.first_name} {open.student?.last_name}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto">
                <Row k="Family" v={`${open.family?.parent_first_name || ""} ${open.family?.parent_last_name || ""} · ${open.family?.email || ""}`} />
                <Row k="Country" v={open.family?.country} />
                <Row k="Program" v={open.program_choice ? t(`programs.${open.program_choice}`) : "—"} />
                <Row k="Grade" v={open.student?.grade} />
                <Row k="French" v={open.student?.french_level} />
                <Row k="English" v={open.student?.english_level} />
                {open.parent_expectations && <div className="bg-secondary/40 p-3 rounded text-xs"><b>Expectations:</b> {open.parent_expectations}</div>}
                {open.special_needs && <div className="bg-secondary/40 p-3 rounded text-xs"><b>Special needs:</b> {open.special_needs}</div>}
                {open.documents?.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-2">Documents</div>
                    <ul className="space-y-1">
                      {open.documents.map((d, i) => <li key={i} className="text-xs border border-border rounded p-2">📎 {d.filename}</li>)}
                    </ul>
                  </div>
                )}
                {/* Evaluation section */}
                {open.evaluation && (
                  <div className="border-2 border-primary/40 bg-primary/5 rounded-lg p-4 space-y-2" data-testid="evaluation-block">
                    <div className="font-semibold text-primary text-xs uppercase tracking-wider">{lang === "fr" ? "Évaluation" : "Evaluation"}</div>
                    <div className="text-xs"><b>{lang === "fr" ? "Envoyée le" : "Sent on"}</b> : {new Date(open.evaluation.sent_at).toLocaleString()} {lang === "fr" ? "par" : "by"} {open.evaluation.sent_by}</div>
                    <div className="text-xs whitespace-pre-line bg-card border border-border rounded p-2">{open.evaluation.message}</div>
                    {open.evaluation.filename && <div className="text-xs">📎 {open.evaluation.filename}</div>}
                    {open.evaluation.received_at ? (
                      <div className="border-t border-primary/30 pt-2 mt-2">
                        <div className="text-xs font-semibold text-accent">{lang === "fr" ? "Évaluation reçue" : "Evaluation received"}</div>
                        <div className="text-xs">{new Date(open.evaluation.received_at).toLocaleString()}</div>
                        {open.evaluation.received_filename && (
                          <div className="text-xs mt-1">📄 {open.evaluation.received_filename}{" "}
                            {open.evaluation.received_base64 && (
                              <a href={open.evaluation.received_base64} download={open.evaluation.received_filename} className="text-primary underline ml-2">{lang === "fr" ? "Télécharger" : "Download"}</a>
                            )}
                          </div>
                        )}
                        {open.evaluation.received_notes && <div className="text-xs italic text-muted-foreground mt-1">"{open.evaluation.received_notes}"</div>}
                      </div>
                    ) : (
                      <div className="border-t border-primary/30 pt-2 mt-2 space-y-1">
                        <div className="text-xs text-muted-foreground italic">{lang === "fr" ? "En attente de retour de la famille…" : "Awaiting family response…"}</div>
                        {open.evaluation.last_reminded_at && (
                          <div className="text-xs text-accent" data-testid="reminder-info">
                            {lang === "fr" ? "Dernier rappel" : "Last reminder"}: {new Date(open.evaluation.last_reminded_at).toLocaleString()}
                            {open.evaluation.reminder_count ? ` · ${open.evaluation.reminder_count}×` : ""}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {!open.evaluation && ["submitted", "under_review"].includes(open.status) && (
                  <Button size="sm" data-testid="send-evaluation-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full" onClick={() => setEvalDialog(true)}>
                    {lang === "fr" ? "Envoyer une évaluation à la famille" : "Send an evaluation to the family"}
                  </Button>
                )}
                <div>
                  <label className="text-xs text-muted-foreground">{t("admin.addNote")}</label>
                  <Textarea data-testid="app-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {STATUSES.map(s => (
                    <Button key={s} size="sm" variant={open.status === s ? "default" : "outline"} data-testid={`app-status-${s}`} onClick={() => setStatus(open.id, s)} className={s === "accepted" ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}>{t(`statuses.${s}`)}</Button>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send evaluation dialog */}
      <Dialog open={evalDialog} onOpenChange={setEvalDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{lang === "fr" ? "Envoyer une évaluation" : "Send evaluation"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{lang === "fr" ? "Message à la famille" : "Message to the family"}</label>
              <Textarea data-testid="eval-message" rows={5} value={evalMessage} onChange={(e) => setEvalMessage(e.target.value)} placeholder={lang === "fr" ? "Bonjour, voici l'évaluation à compléter dans 7 jours…" : "Hello, please complete the attached evaluation within 7 days…"} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{lang === "fr" ? "Pièce jointe (PDF, image — max 2,5 Mo)" : "Attachment (PDF/image — max 2.5MB)"} {evalFile && `· ${evalFile.name}`}</label>
              <input type="file" onChange={onPickEvalFile} accept="application/pdf,image/*" data-testid="eval-file" className="block w-full text-sm" />
            </div>
            <Button data-testid="eval-send-confirm" onClick={sendEvaluation} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
              {lang === "fr" ? "Envoyer l'évaluation" : "Send evaluation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between border-b border-border/50 py-1.5"><span className="text-muted-foreground">{k}</span><span>{v || "—"}</span></div>;
}
